import { useCallback, useRef, useEffect } from 'react';

interface UseDebounceOptions {
    delay?: number;
}

/**
 * A hook that provides debounced function execution with optional callbacks for success/error states
 * @param asyncFunction - The async function to debounce
 * @param delay - Debounce delay in milliseconds (default: 1000)
 * @returns A debounced function that accepts arguments for the async function
 */
export function useDebounce<T extends (...args: any[]) => Promise<void>>(
    asyncFunction: T,
    { delay = 1000 }: UseDebounceOptions = {}
) {
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Cleanup debounce timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    const debouncedFn = useCallback(
        async (...args: Parameters<T>) => {
            // Clear any pending execution
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }

            debounceTimerRef.current = setTimeout(async () => {
                await asyncFunction(...args);
            }, delay);
        },
        [asyncFunction, delay]
    );

    return debouncedFn;
}
